'use client'

import { ButtonInspirational } from '@fukku/button/Inspirational/ButtonInspirational'
import { BottomSheet } from '@fukku/dialog/BottomSheet/BottomSheet'
import { Content } from '@fukku/dialog/Content/Content'
import type { DialogAPI } from '@fukku/dialog/Dialog'
import { Header } from '@fukku/dialog/Header/Header'
import {
	SideSheet,
	type SideSheetProps,
} from '@fukku/dialog/SideSheet/SideSheet'
import { useLabels } from 'labels/hooks/useLabels/useLabels'
import { MapProvider } from 'maps/contexts/MapProvider'
import { getStoresRFID } from 'maps/services/client/getStoresRFID/getStoresRFID'
import { type Coordinates, MAP_TYPE } from 'maps/types'
import { getCurrentPosition } from 'maps/utils/geolocation/geolocation'
import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import dynamic from 'next/dynamic'
import {
	type ForwardRefExoticComponent,
	type RefAttributes,
	useRef,
	useState,
} from 'react'
import { useResponsiveBehavior } from 'responsive/hooks/useResponsiveBehavior/useResponsiveBehavior'
import { dataTestIds } from '../../constants/dataTestIds/dataTestIds'
import { useAnalytics } from '../../contexts/AnalyticsProvider/AnalyticsProvider'
import { useColors } from '../../contexts/ColorsProvider/ColorsProvider'
import type { CommonProps } from '../../types/CommonProps'
import { PdpActionEvents } from '../../types/PdpActionEvents'

const DynamicStoreFinderBody = dynamic(() =>
	import('./StoreFinderBody/StoreFinderBody').then((mod) => mod.StoreFinderBody)
)
export type StoreFinderProps = Readonly<Pick<CommonProps, 'product'>> & {
	className?: string
}

export function StoreFinder({
	product,
	className,
}: Readonly<StoreFinderProps>) {
	const { brandId } = product
	const { t } = useLabels()
	const { sendPdpActionEvent } = useAnalytics()
	const modalRef = useRef<DialogAPI>(null)
	const { selectedColor } = useColors()
	const [coordinates, setCoordinates] = useState<Coordinates | null>(null)
	const {
		country: { countryCode, languageISO },
	} = useMasterData()

	const ResponsiveDialog = useResponsiveBehavior<
		ForwardRefExoticComponent<SideSheetProps & RefAttributes<DialogAPI>>
	>({
		default: SideSheet,
		small: BottomSheet,
	})

	const open = async () => {
		try {
			const userCoordinates = await getCurrentPosition()
			setCoordinates(userCoordinates)
		} catch (_error) {}
		sendPdpActionEvent(PdpActionEvents.AVAILABILITY_STORE)
		modalRef.current?.show()
	}

	if (!brandId) {
		return null
	}

	return (
		<>
			<ButtonInspirational
				onClick={open}
				data-testid={dataTestIds['pdp.productInfo.storeFinder']}
				className={className}
			>
				{t('pdp.storeAvailability.link')}
			</ButtonInspirational>
			<ResponsiveDialog
				title={t('storeLocator.storeAvailabilty.title')}
				ref={modalRef}
				extraWidth
				data-testid={dataTestIds['pdp.productInfo.storeFinder.dialog']}
			>
				<Header title={t('storeLocator.storeAvailabilty.title')} />
				<MapProvider
					initialState={{
						type: MAP_TYPE.RFID_STORES,
						onModal: true,
						nearMe: false,
						location: { coordinates: coordinates ?? undefined },
						fetchStores: (storeLocation) =>
							getStoresRFID({
								productId: product.id,
								colorId: selectedColor.id,
								country: countryCode,
								language: languageISO,
								latitude: storeLocation.coordinates?.lat.toString() ?? '0.0',
								longitude: storeLocation.coordinates?.lng.toString() ?? '0.0',
							}),
					}}
				>
					<Content>
						<DynamicStoreFinderBody
							productId={product.id}
							colorId={selectedColor.id}
							coordinates={coordinates}
						/>
					</Content>
				</MapProvider>
			</ResponsiveDialog>
		</>
	)
}
